// DoctorDashboard.jsx - Main doctor view with patient list and patient detail overlay
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAuthSession, fetchUserAttributes } from '../util/amplifyAuth';
import { listDoctorPatients, retrieveDoctorConfig } from '../components/API';
import { setLabDefaults } from '../data/defaultTestData';
import { formatDateISO, getInitials } from '../util/GenericUtil';
import useOrderedList from '../hooks/useOrderedList';
import UserHeader from '../components/UserHeader/UserHeader';
import CreatePatientModal from '../components/CreatePatientModal';
import Home from './Home';
import HelixLoader from '../components/HelixLoader/HelixLoader';
import '../styles/DoctorDashboard.css';

export default function DoctorDashboard() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [doctorInstitution, setDoctorInstitution] = useState('');
  const [userId, setUserId] = useState(null);
  const tabPortalRef = useRef(null);
  const [exportPortalNode, setExportPortalNode] = useState(null);
  const [portalReady, setPortalReady] = useState(false);

  const { searchQuery, setSearchQuery, displayedItems: displayedPatients, canDrag, dragProps } =
    useOrderedList(patients, { storageKey: 'bloodeye:patientOrder', userId });

  useEffect(() => {
    loadPatients();
  }, []);

  async function loadPatients() {
    try {
      setLoading(true);
      setError(null);

      const session = await fetchAuthSession();
      const groups = session.tokens?.idToken?.payload['cognito:groups'] || [];

      // Verify user is a doctor
      if (!groups.includes('Doctors')) {
        navigate('/home');
        return;
      }

      const sub = session.tokens?.idToken?.payload?.sub;
      if (sub) setUserId(sub);

      const [patientsResult, doctorConfig, attrs] = await Promise.all([
        listDoctorPatients(),
        retrieveDoctorConfig(),
        fetchUserAttributes().catch(() => null)
      ]);

      if (attrs) setDoctorInstitution(attrs['custom:institution'] || '');
      if (doctorConfig?.labDefaults) setLabDefaults(doctorConfig.labDefaults);
      setPatients(patientsResult.patients || []);
    } catch (err) {
      console.error('Error loading patients:', err);
      setError('Failed to load patient list');
    } finally {
      setLoading(false);
    }
  }

  function handlePatientSelect(patient) {
    setSelectedPatient(patient);
  }

  function handleBackToList() {
    setSelectedPatient(null);
  }

  return (
    <div className="doctor-dashboard">
      <UserHeader
        sharingMode="doctor"
        sharingLabel={selectedPatient ? formatPatientName(selectedPatient) : null}
        onBack={selectedPatient ? handleBackToList : null}
        tabControl={selectedPatient ? <div ref={el => { tabPortalRef.current = el; if (el && !portalReady) setPortalReady(true); }} style={{ display: 'contents' }} /> : null}
        exportButton={selectedPatient ? <div ref={setExportPortalNode} style={{ display: 'contents' }} /> : null}
      >
        <a onClick={() => navigate('/home')} className="header-link">Home</a>
      </UserHeader>

      <div className="doctor-content">
        {/* Patient List Sidebar */}
        <aside className={`patient-list-sidebar ${selectedPatient ? 'collapsed' : ''}`}>
          <div className="sidebar-header">
            <h2>My Patients</h2>
            <div className="sidebar-header-actions">
              <button
                className="btn-icon btn-add"
                onClick={() => setShowCreateModal(true)}
                title="Add patient"
              >
                +
              </button>
              <button
                className="btn-icon"
                onClick={loadPatients}
                title="Refresh patient list"
              >
                ↻
              </button>
            </div>
          </div>

          <div className="sidebar-body">
            {loading && (
              <div className="loading-state">
                <HelixLoader dotCount={15} gap={2} />
              </div>
            )}

            {error && (
              <div className="error-state">
                <p>{error}</p>
                <button onClick={loadPatients}>Try Again</button>
              </div>
            )}

            {!loading && !error && patients.length > 0 && (
              <div className="sidebar-search">
                <input
                  type="text"
                  placeholder="Search patients..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </div>
            )}

            {!loading && !error && patients.length === 0 && (
              <div className="empty-state">
                <p>No patients have granted you access yet.</p>
                <p className="hint">Patients can connect with you using your doctor code.</p>
              </div>
            )}

            {!loading && !error && patients.length > 0 && displayedPatients.length === 0 && (
              <div className="empty-state">
                <p>No matching patients "{searchQuery}"</p>
              </div>
            )}

            {!loading && !error && displayedPatients.length > 0 && (
              <ul className="patient-list">
                {displayedPatients.map((patient, index) => (
                  <li
                    key={patient.patientId}
                    className={`patient-card ${selectedPatient?.patientId === patient.patientId ? 'active' : ''}`}
                    onClick={() => handlePatientSelect(patient)}
                    {...dragProps(index)}
                  >
                    <div className="patient-card-header">
                      <div className="patient-initials">
                        {getInitials(patient.patientName, 'P')}
                      </div>
                      <div className="patient-info">
                        <h3>{formatPatientName(patient)}</h3>
                        <p className="patient-id">ID: {patient.patientId?.substring(0, 8)}...</p>
                      </div>
                    </div>

                    <div className="patient-card-meta">
                      <span className="meta-item">
                        <span className="meta-label">Access granted:</span>
                        <span className="meta-value">{formatDateISO(patient.grantedAt)}</span>
                      </span>
                      <span className="meta-item">
                        <span className="meta-label">Expires:</span>
                        <span className="meta-value expires">{formatDateISO(patient.expiresAt)}</span>
                      </span>
                      {patient.accessReason && (
                        <span className="meta-item">
                          <span className="meta-label">Reason:</span>
                          <span className="meta-value">{patient.accessReason}</span>
                        </span>
                      )}
                    </div>

                    <div className="patient-card-arrow">›</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>

        {/* Patient View or Welcome */}
        {selectedPatient ? (
          <div className="patient-view-body">
            <div className="patient-view-content">
              <Home
                doctorViewMode={true}
                patientId={selectedPatient.patientId}
                patientName={selectedPatient.patientName}
                tabPortalRef={portalReady ? tabPortalRef : null}
                exportPortalRef={exportPortalNode}
              />
            </div>
          </div>
        ) : (
          <div className="doctor-welcome">
            <div className="doctor-welcome-content">
              <div className="doctor-welcome-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 00-3-3.87" />
                  <path d="M16 3.13a4 4 0 010 7.75" />
                </svg>
              </div>
              <h2>Doctor Portal</h2>
              <p>Select a patient from the sidebar to view their lab results, health scores, and protocol recommendations.</p>
              <div className="doctor-welcome-hints">
                <div className="welcome-hint">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                  </svg>
                  <span>Review trends and markers across test dates</span>
                </div>
                <div className="welcome-hint">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                  <span>Manage supplement protocols and lock recommendations</span>
                </div>
                <div className="welcome-hint">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                  </svg>
                  <span>Chat with Elis about patient-specific insights</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {showCreateModal && (
        <CreatePatientModal
          institution={doctorInstitution}
          onClose={() => setShowCreateModal(false)}
          onCreated={loadPatients}
        />
      )}
    </div>
  );
}

// Helper functions
function formatPatientName(patient) {
  if (patient.patientName) {
    const parts = patient.patientName.trim().split(/\s+/);
    if (parts.length >= 2) {
      const lastName = parts[parts.length - 1];
      const firstName = parts.slice(0, -1).join(' ');
      return `${lastName}, ${firstName}`;
    }
    return patient.patientName;
  }
  return `Patient ${patient.patientId?.substring(0, 8)}`;
}
